import React, { useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { replaceDeliveryOrderBatch } from '../../deliveryOrderBatches/deliveryOrderBatchesActions';
import TextareaAutosize from 'react-autosize-textarea';
import utils from '../../utils/utils';
import AdminDeliveryDayViewDeliveryBatchTable from './adminDeliveryDayViewDeliveryBatchTable';
import { pdf } from '@react-pdf/renderer';
import AdminDeliveryDayViewDeliveriesPdfDocument from './adminDeliveryDayViewDeliveriesPdfDocument';
import { saveAs } from 'file-saver';

const AdminDeliveryDayViewDeliveries = ({
  setModalOrder,
  filteredOrderBatches,
  intl,
  setModalOrderBatch,
  deliveryOrderBatchStatusOptions,
  deliveryDay,
  selectedTemperature,
}) => {
  const [rowsInput, setRowsInput] = useState('');

  const deliveryRows = rowsInput
    .split('\n')
    .filter((row) => {
      return /[a-zA-Z]/g.test(row);
    })
    .map((row) => {
      const fields = row.split('\t');

      const routeId = fields.length === 3 ? fields[2] : null;
      return {
        id: fields[0],
        driver: fields[1],
        routeId: routeId,
      };
    });

  const groupedDeliveries = utils.groupBy(deliveryRows, 'driver');

  return (
    <div className="m-b-6">
      <div className="m-b-2">
        <Link className="btn btn-default" to={`/admin/deliveryDay/${deliveryDay ? deliveryDay.id : ''}`}>
          <FormattedMessage id={'delivery.day.users.view.cta.orders'} />
        </Link>
      </div>

      <div className="form-group">
        <label className="control-label">
          <FormattedMessage id={'delivery.day.view.deliveries.field.label'} />
        </label>

        <TextareaAutosize
          onChange={(event) => setRowsInput(event.target.value)}
          className="form-control"
          rows={2}
          maxRows={4}
          value={rowsInput}
        />
        <p className="help-block">
          <FormattedMessage id={'delivery.day.view.deliveries.field.help'} />
        </p>
      </div>

      <div className="m-b-6 m-t-6">
        {Object.keys(groupedDeliveries).map((key) => {
          const packingIds = groupedDeliveries[key];
          const routeId = packingIds.length ? packingIds[0].routeId : null;

          const deliveryOrderBatches = [];
          packingIds.forEach((packingId) => {
            const orderBatch = filteredOrderBatches.find((fob) => fob.packingId === packingId.id);
            if (orderBatch) {
              deliveryOrderBatches.push(orderBatch);
            } else {
              deliveryOrderBatches.push({
                user: {
                  id: packingId.id,
                },
                orders: [
                  {
                    items: [],
                  },
                ],
              });
            }
          });

          let fruitCount = 0;
          let vegCount = 0;
          deliveryOrderBatches.forEach((dob) => {
            const fruitMarketplaceOrders = dob.orders.filter((o) => o.marketplaceId === 74);
            fruitMarketplaceOrders.forEach((o) => {
              o.items.forEach((oi) => {
                if (oi.item.id === 898) {
                  fruitCount += oi.quantity;
                  vegCount += oi.quantity;
                } else if (oi.item.id === 897) {
                  fruitCount += oi.quantity;
                } else if (oi.item.id === 896) {
                  vegCount += oi.quantity;
                }
              });
            });
          });

          return (
            <div className="m-b-6">
              <h3 className="m-b-3">
                🚗{' '}
                {routeId ? (
                  <>
                    <FormattedMessage id={'delivery.day.view.deliveries.route.id'} values={{ routeId }} />
                    {' - '}
                  </>
                ) : (
                  ''
                )}
                {key}
              </h3>

              <div className="m-b-1">
                <FormattedMessage id={'delivery.day.view.deliveries.driver.fruit.count'} />:{' '}
                <strong className={packingIds.length !== deliveryOrderBatches.length ? 'text-warning' : ''}>
                  {fruitCount}
                </strong>
              </div>
              <div className="m-b-3">
                <FormattedMessage id={'delivery.day.view.deliveries.driver.veg.count'} />:{' '}
                <strong className={packingIds.length !== deliveryOrderBatches.length ? 'text-warning' : ''}>
                  {vegCount}
                </strong>
              </div>

              {packingIds.length !== deliveryOrderBatches.length ? (
                <div className="alert alert-warning">
                  <FormattedMessage id={'delivery.day.view.deliveries.driver.order.batches.length.not.matching'} />
                </div>
              ) : (
                ''
              )}

              <div className="m-b-6">
                <AdminDeliveryDayViewDeliveryBatchTable
                  setModalOrder={setModalOrder}
                  setModalOrderBatch={setModalOrderBatch}
                  filteredOrderBatches={deliveryOrderBatches}
                  selectedTemperature={selectedTemperature}
                  deliveryOrderBatchStatusOptions={deliveryOrderBatchStatusOptions}
                />
              </div>
              <div>
                <button
                  className="btn btn-primary m-b-3"
                  onClick={async () => {
                    const doc = (
                      <AdminDeliveryDayViewDeliveriesPdfDocument
                        deliveryOrderBatches={deliveryOrderBatches}
                        copy={utils.getCourierPdfCopy(intl)}
                        courierName={key}
                        totalFruits={fruitCount}
                        totalVegs={vegCount}
                        routeId={routeId}
                      />
                    );
                    const asPdf = pdf(); // {} is important, throws without an argument
                    asPdf.updateContainer(doc);
                    const blob = await asPdf.toBlob();
                    saveAs(blob, `${'courier-pdf'}.pdf`);
                  }}
                >
                  <FormattedMessage id={'delivery.day.view.deliveries.driver.download.pdf'} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

AdminDeliveryDayViewDeliveries.propTypes = {
  filteredOrderBatches: PropTypes.array,
  deliveryOrderBatchStatusOptions: PropTypes.array.isRequired,
  setModalOrder: PropTypes.func.isRequired,
  setModalOrderBatch: PropTypes.func.isRequired,
  deliveryDay: PropTypes.object.isRequired,
  selectedTemperature: PropTypes.string,
};

AdminDeliveryDayViewDeliveries.defaultProps = {};

function mapStateToProps(state) {
  return {
    deliveryOrderBatches: state.deliveryOrderBatches.deliveryOrderBatches,
    currentUser: state.currentUser.user,
  };
}

const mapDispatchToProps = {
  replaceDeliveryOrderBatch,
};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(AdminDeliveryDayViewDeliveries));
